import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { loginUtente } from "../../lib/services/authService";
import { useNavigate } from 'react-router-dom';

const LoginModal = ({ isOpen, onClose, onSuccess, onForgotPassword }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = t('auth.errors.email_req');
    if (!formData.password) newErrors.password = t('auth.errors.pass_req');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const result = await loginUtente(formData.email, formData.password);
      if (onSuccess) onSuccess(result);
      handleClose();
    } catch (err) {
      console.error("Login error:", err.message);
      if (err.message === "ACCOUNT_DEACTIVATED") {
        handleClose();
        navigate('/not-active');
      } else if (err.message === "COMPANY_NOT_ACTIVE") {
        handleClose();
        navigate('/company-not-active');
      } else {
        setErrors({ general: t('auth.errors.invalid_credentials') || "Email o password errati" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setFormData({ email: '', password: '' });
    setErrors({});
    setIsLoading(false);
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[2px] transition-opacity animate-in fade-in duration-200 px-4" 
      onClick={handleClose}
    >
      <div 
        className="modal-content w-full max-w-md bg-card rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header p-6 flex justify-between items-center border-b border-border">
          <h2 className="modal-title font-black uppercase text-sm tracking-widest text-foreground">
            {t('auth.login')}
          </h2>
          <button 
            onClick={handleClose} 
            className="p-2 hover:bg-muted rounded-xl transition-colors text-muted-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        
        <div className="p-6 pb-10">
          {errors.general && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl animate-in shake duration-300">
              <p className="text-red-500 text-[11px] text-center font-bold tracking-wide uppercase">
                {errors.general}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input 
              type="email" 
              name="email" 
              label={t('auth.email')} 
              value={formData.email} 
              onChange={handleChange} 
              error={errors.email} 
              required 
              placeholder="example@mail.com"
            />
            
            <div className="relative">
              <Input 
                type={showPassword ? 'text' : 'password'}
                name="password" 
                label={t('auth.password')} 
                value={formData.password} 
                onChange={handleChange} 
                error={errors.password} 
                required 
                placeholder="••••••••"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-4 bottom-3 text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
              </button>
            </div>

            <div className="pt-4 flex flex-col gap-4">
              <Button 
                type="submit" 
                fullWidth 
                loading={isLoading} 
                className="shadow-lg shadow-primary/20 py-4"
              >
                {t('auth.login')}
              </Button>

              <button 
                type="button"
                className="text-center text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors py-2"
                onClick={onForgotPassword}
              >
                {t('auth.forgot_password')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;